<script setup lang="ts">
// import FundIcon from '~/assets/images/exchange-detail/iap/fund.png'

const emit = defineEmits<{
  open: []
  openIap: []
}>()

const { traderData } = defineProps<{
  traderData: any
}>()

const { t, locale } = useAppI18n()
const iap = computed(() => {
  return !!traderData?.bitGuarantee
})

const open = () => {
  emit('open')
}

const openIap = () => {
  emit('openIap')
}

const headerRef = ref<HTMLElement | null>(null)
const measureSpanRef = ref<HTMLElement | null>(null)

// 1. SSR: Estimate text width based on CJK/Latin to avoid initial flash
const getInitialShowFullName = () => {
  if (traderData?.bitGuarantee?.type !== 0) return false
  const name = traderData?.basicInfo?.shortNameLocal || ''
  const fullText = t('83728', [name])

  let approxWidth = 0
  for (let i = 0; i < fullText.length; i++) {
    const code = fullText.charCodeAt(i)
    // Check if character is CJK (Chinese, Japanese, Korean)
    if (
      (code >= 0x4e00 && code <= 0x9fff) || // CJK Ideographs
      (code >= 0x3040 && code <= 0x30ff) || // Hiragana/Katakana
      (code >= 0xac00 && code <= 0xd7a3) // Hangul
    ) {
      approxWidth += 18
    } else {
      approxWidth += 10 // Latin, Cyrillic, Arabic, Thai, etc.
    }
  }
  return approxWidth <= 140
}

const showFullName = ref(getInitialShowFullName())

let resizeObserver: ResizeObserver | null = null

// 2. Client: Exact DOM measurement to correct the guess
const checkOverflow = () => {
  if (!measureSpanRef.value) return
  showFullName.value = measureSpanRef.value.scrollWidth <= measureSpanRef.value.clientWidth
}

onMounted(() => {
  if (traderData?.bitGuarantee?.type === 0) {
    checkOverflow()
    resizeObserver = new ResizeObserver(() => {
      checkOverflow()
    })
    if (headerRef.value) {
      resizeObserver.observe(headerRef.value)
    }
  }
})

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
})
</script>

<template>
  <section class="exchange-emc-card" :class="{ 'exchange-emc-card--iap': iap, 'is-ru': locale === 'ru' }">
    <div v-if="iap" class="exchange-emc-card__iap-layout">
      <div
        class="exchange-emc-card__iap"
        role="button"
        tabindex="0"
        aria-labelledby="iap-title"
        @click.stop="openIap"
        @keydown.enter.stop.prevent="openIap"
        @keydown.space.stop.prevent="openIap"
      >
        <header class="exchange-emc-card__header relative" ref="headerRef">
          <h2 id="iap-title" class="exchange-emc-card__title" style="min-width: 0; flex: 1">
            <span
              :style="{
                color: traderData?.bitGuarantee?.type === 0 ? '#1f845a' : '#112D73',
              }"
              style="flex-shrink: 0"
              >IAP</span
            >
            <span
              v-if="traderData?.bitGuarantee?.type === 0"
              style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1"
              >{{ t('83728', showFullName ? [traderData?.basicInfo?.shortNameLocal] : []) }}</span
            >
            <span v-else style="flex-shrink: 0">{{ t('83729') }} </span>
          </h2>
          <!-- <CommonImage
            class="h-[16px] w-[16px]"
            style="flex-shrink: 0"
            src="https://resources1.interface003.com/web30/2026/06/15/af37c367-a6b0-4de1-8ca8-b7f395b44db4.png"
          /> -->
          <!-- Hidden measurement clone -->
          <h2
            class="exchange-emc-card__title pointer-events-none absolute opacity-0"
            style="min-width: 0; flex: 1; left: 0; top: 0; width: calc(100% - 20px); z-index: -1"
            aria-hidden="true"
          >
            <span style="flex-shrink: 0">IAP</span>
            <span
              v-if="traderData?.bitGuarantee?.type === 0"
              ref="measureSpanRef"
              style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1"
              >{{ t('83728', [traderData?.basicInfo?.shortNameLocal]) }}</span
            >
            <span v-else style="flex-shrink: 0">{{ t('83729') }} </span>
          </h2>
        </header>

        <div class="exchange-emc-card__amount">
          <span class="exchange-emc-card__currency" aria-hidden="true">
            <CommonImage
              v-if="traderData?.bitGuarantee?.exchangeIcon || traderData?.bitGuarantee?.platformIcon"
              :src="
                traderData?.bitGuarantee.type === 0
                  ? traderData?.bitGuarantee?.exchangeIcon
                  : traderData?.bitGuarantee?.platformIcon
              "
              alt=""
            />
          </span>
          <strong class="font-wiki-Bold">{{ traderData?.bitGuarantee?.exchangeGuaranteeAmount }}</strong>
        </div>
      </div>

      <div class="exchange-emc-card__divider" aria-hidden="true"></div>

      <div
        class="exchange-emc-card__iap-emc"
        role="button"
        tabindex="0"
        aria-labelledby="exchange-emc-title"
        @click.stop="open"
        @keydown.enter.stop.prevent="open"
        @keydown.space.stop.prevent="open"
      >
        <header class="exchange-emc-card__header">
          <h2 id="exchange-emc-title" class="exchange-emc-card__title">
            <span class="exchange-emc-card__brand shrink-0">EMC</span>
            <span class="truncate">{{ t('84584') }}</span>
          </h2>
          <!-- <CommonImage
            class="h-[16px] w-[16px]"
            src="https://resources1.interface003.com/web30/2026/06/15/af37c367-a6b0-4de1-8ca8-b7f395b44db4.png"
          /> -->
        </header>

        <div class="exchange-emc-card__response">
          <strong class="exchange-emc-card__days">7</strong>
          <span class="truncate">{{ t('84591') }}</span>
        </div>
      </div>
    </div>

    <div
      v-else
      class="exchange-emc-card__legacy"
      role="button"
      tabindex="0"
      aria-labelledby="exchange-emc-title"
      @click="open"
      @keydown.enter.prevent="open"
      @keydown.space.prevent="open"
    >
      <header class="exchange-emc-card__header">
        <h2 id="exchange-emc-title" class="exchange-emc-card__title">
          <span class="exchange-emc-card__brand shrink-0">EMC</span>
          <span class="truncate">{{ t('84584') }}</span>
        </h2>
      </header>

      <div class="exchange-emc-card__body">
        <div class="exchange-emc-card__response">
          <strong class="exchange-emc-card__days">7</strong>
          <span class="truncate">{{ t('84591') }}</span>
        </div>

        <div class="exchange-emc-card__features">
          <div class="exchange-emc-card__pill">
            <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M14.6667 11.3347C14.6664 12.2464 14.3548 13.1307 13.7834 13.8411C13.212 14.5516 12.4152 15.0456 11.5247 15.2413L11.0993 13.9653C11.4888 13.9012 11.8592 13.7515 12.1839 13.5269C12.5085 13.3023 12.7793 13.0085 12.9767 12.6667H11.3333C10.9797 12.6667 10.6406 12.5262 10.3905 12.2761C10.1405 12.0261 10 11.687 10 11.3333V8.66667C10 8.31304 10.1405 7.97391 10.3905 7.72386C10.6406 7.47381 10.9797 7.33333 11.3333 7.33333H13.292C13.1294 6.04459 12.502 4.85951 11.5277 4.00045C10.5533 3.14138 9.299 2.66738 8 2.66738C6.70104 2.66738 5.44672 3.14138 4.47238 4.00045C3.49805 4.85951 2.87068 6.04459 2.708 7.33333H4.66667C5.02029 7.33333 5.35943 7.47381 5.60948 7.72386C5.85952 7.97391 6 8.31304 6 8.66667V11.3333C6 11.687 5.85952 12.0261 5.60948 12.2761C5.35943 12.5262 5.02029 12.6667 4.66667 12.6667H2.66667C2.31304 12.6667 1.97391 12.5262 1.72386 12.2761C1.47381 12.0261 1.33333 11.687 1.33333 11.3333V8C1.33333 4.318 4.318 1.33333 8 1.33333C11.682 1.33333 14.6667 4.318 14.6667 8V11.3347Z"
                fill="currentColor"
              />
            </svg>
            <span class="truncate">{{ t('84604') }}</span>
          </div>

          <div class="exchange-emc-card__pill">
            <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M8 14.6667C4.318 14.6667 1.33333 11.682 1.33333 8C1.33333 4.318 4.318 1.33333 8 1.33333C11.682 1.33333 14.6667 4.318 14.6667 8C14.6667 11.682 11.682 14.6667 8 14.6667ZM8.66667 8V4.66667H7.33333V9.33333H11.3333V8H8.66667Z"
                fill="currentColor"
              />
            </svg>
            <span class="truncate">{{ t('84596') }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="less">
.exchange-emc-card {
  display: flex;
  min-height: 212px;
  width: 100%;
  flex-direction: column;
  gap: 20px;
  border: 0.5px solid rgba(11, 18, 40, 0.14);
  border-radius: 12px;
  background: linear-gradient(180deg, #f5faf8 0%, #fff 80%);
  cursor: pointer;
  padding: 20px;
  transition:
    box-shadow 0.2s ease,
    transform 0.2s ease;
}
.exchange-emc-card:hover {
  box-shadow: 0 12px 32px rgba(8, 19, 62, 0.08);
}

// .exchange-emc-card__iap:hover,
// .exchange-emc-card__iap-emc:hover,
// .exchange-emc-card__legacy:hover {
//   box-shadow: 0 12px 32px rgba(8, 19, 62, 0.08);
// }

.exchange-emc-card__iap:focus-visible,
.exchange-emc-card__iap-emc:focus-visible,
.exchange-emc-card__legacy:focus-visible {
  outline: 2px solid rgba(31, 132, 90, 0.24);
  outline-offset: 2px;
}

.exchange-emc-card--iap {
  height: 212px;
  min-height: 212px;
  gap: 16px;
}

.exchange-emc-card__iap-layout,
.exchange-emc-card__legacy {
  display: flex;
  min-height: 0;
  width: 100%;
  flex: 1;
  flex-direction: column;
}

.exchange-emc-card__iap-layout {
  gap: 16px;
}

.exchange-emc-card__legacy {
  gap: 20px;
}

.exchange-emc-card__header {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 4px;
}

.exchange-emc-card__title {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  color: #292a2e;
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
}

.exchange-emc-card__brand {
  color: #1f845a;
}

.exchange-emc-card.is-ru .exchange-emc-card__brand {
  font-size: 16px;
}

.exchange-emc-card__info-icon {
  width: 16px;
  height: 16px;
  flex: 0 0 auto;
  color: #7d818a;
}

.exchange-emc-card__iap {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 12px;
}

.exchange-emc-card__amount {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 8px;
  color: #292a2e;
  font-family: WikiFX, Outfit, sans-serif;
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
}

.exchange-emc-card__currency {
  display: flex;
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #fff;
}

.exchange-emc-card__currency img {
  width: 24px;
  height: 24px;
  border-radius: 50%;
}

.exchange-emc-card__divider {
  width: 100%;
  height: 0.5px;
  flex: 0 0 auto;
  background: rgba(11, 18, 40, 0.08);
}

.exchange-emc-card__iap-emc {
  display: flex;
  min-height: 0;
  width: 100%;
  flex: 1;
  flex-direction: column;
  gap: 16px;
}

.exchange-emc-card__body {
  display: flex;
  min-height: 0;
  width: 100%;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
}

.exchange-emc-card__response {
  display: flex;
  width: 100%;
  align-items: baseline;
  gap: 4px;
  color: #292a2e;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  white-space: nowrap;
}

.exchange-emc-card__days {
  color: #1f845a;
  font-family: WikiFX-Bold;
  font-size: 40px;
  font-weight: 700;
  line-height: 1;
}

.exchange-emc-card--iap .exchange-emc-card__days {
  font-size: 30px;
  font-weight: 900;
  line-height: 38px;
}

.exchange-emc-card__features {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 8px;
}

.exchange-emc-card__pill {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 4px;
  border: 0.5px solid rgba(5, 21, 36, 0.06);
  border-radius: 100px;
  background: #fff;
  padding: 4px 12px;
  color: #505258;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  white-space: nowrap;
}

.exchange-emc-card__pill svg {
  width: 16px;
  height: 16px;
  flex: 0 0 auto;
}
</style>
